import React from "react";
import Icon from "./AppIcon";

// Brand / marketing panel shared by the auth screens (Login + Signup).
// Uses the prepxai brand primary green (#169873) as a solid fill so the portal
// reads as the same product as the consumer app and landing page.
//
// On a tenant host it can be co-branded: pass `logoUrl` (absolute) and
// `institutionName` and the panel leads with the institution's own logo + name,
// with a small "powered by prepxai" mark, so an admin signing in at
// <slug>.prepxai.in sees their own identity first.
export default function BrandPanel({ logoUrl = "", institutionName = "" }) {
  const branded = Boolean(logoUrl || institutionName);
  const points = [
    { icon: "Users", text: "Onboard entire placement batches in minutes" },
    { icon: "Send", text: "Assign AI mock interviews to hundreds of students at once" },
    { icon: "BarChart3", text: "Track completion and performance across your cohort" },
  ];
  return (
    <div className="relative hidden overflow-hidden bg-primary lg:flex lg:w-1/2">
      <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10" />
      <div className="absolute -bottom-20 -left-10 h-72 w-72 rounded-full bg-white/10" />
      <div className="relative z-10 flex w-full flex-col justify-between p-12 text-white">
        <div className="flex items-center gap-2.5">
          {logoUrl ? (
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white p-2 shadow-sm">
                <img
                  src={logoUrl}
                  alt={institutionName ? `${institutionName} logo` : "Institution logo"}
                  className="h-full w-full object-contain"
                />
              </div>
              {institutionName && (
                <div className="leading-tight">
                  <div className="text-3xl font-semibold [text-wrap:balance]">{institutionName}</div>
                  <div className="mt-1 text-base text-white/80">on prepxai</div>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/15">
                <Icon name="GraduationCap" size={22} color="#ffffff" />
              </div>
              <div className="leading-tight">
                <div className="text-lg font-semibold">{institutionName || "prepxai"}</div>
                <div className="-mt-0.5 text-xs text-white/80">
                  {institutionName ? "on prepxai" : "for institutions"}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="max-w-md">
          <h1 className="text-3xl font-semibold leading-tight text-white">
            {branded
              ? "Welcome back to your placement console."
              : "Placement prep, run at the scale of your whole campus."}
          </h1>
          <p className="mt-4 text-white/85">
            The institution console for prepxai — manage students, launch
            interview campaigns, and measure readiness from one place.
          </p>
          <ul className="mt-8 space-y-4">
            {points.map((p) => (
              <li key={p.text} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white/15">
                  <Icon name={p.icon} size={14} color="#ffffff" />
                </span>
                <span className="text-sm text-white/90">{p.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs text-white/70">
          © {new Date().getFullYear()} prepxai. All rights reserved.
        </p>
      </div>
    </div>
  );
}
